import {
  ValidationErrors,
  AbstractControl,
  ValidatorFn,
  UntypedFormArray,
  UntypedFormGroup,
} from "@angular/forms";
import { ItemMinAndMax } from "../model/inventory";

export class CustomValidators {
  static readonly applicationTagMaxLength = 50;
  static readonly applicationTagPattern = new RegExp(
    `^[A-Za-z0-9][A-Za-z0-9 .'_-]{0,${CustomValidators.applicationTagMaxLength - 1}}$`
  );
  static readonly applicationTagRequiredMessage = 'Tag is required';
  static readonly applicationTagLengthMessage = `Tag must be ${CustomValidators.applicationTagMaxLength} characters or fewer`;
  static readonly applicationTagCharsetMessage =
    'Tag can only contain letters, numbers, spaces, hyphens, underscores, and periods';

  /** Collapse whitespace so "Sai  Music" matches typed/pasted "Sai Music". */
  static normalizeWhitespace(value: unknown): string {
    return String(value ?? '').replace(/\s+/g, ' ').trim();
  }

  static PatternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        // if control is empty return no error
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }
  
  static EnhancedPatternValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      const errors: ValidationErrors = {};
      // Check for minimum length
      if (value.length < 12) {
        errors['minLengthError'] = true;
      }
      // Check for maximum length
      if (value.length > 25) {
        errors['maxLengthError'] = true;
      }
      // Check for at least one uppercase letter
      if (!/[A-Z]/.test(value)) {
        errors['missingUppercase'] = true;
      }
      // Check for at least one lowercase letter
      if (!/[a-z]/.test(value)) {
        errors['missingLowercase'] = true;
      }
      // Check for at least one number
      if (!/[0-9]/.test(value)) {
        errors['missingNumber'] = true;
      }
      // Check for at least one special character
      if (!/[!@#\$%\^&\*]/.test(value)) {
        errors['missingSpecialCharacter'] = true;
      }
      // Return errors if any
      return Object.keys(errors).length ? errors : null;
    };
  }

  static PassswordMatch(control: AbstractControl) {
    const password: string = control.get("password").value;
    const confirmPassword: string = control.get("cpassword").value;
    if (password !== confirmPassword) {
      control.get("cpassword").setErrors({ NoPassswordMatch: true });
    }
  }

  static CompareTwoValues(itemToCompare: []) {
    return (control: AbstractControl): ValidationErrors | null => {
      const itemToCompareValueLen = itemToCompare.length;
      const controlValueLen = control?.value?.length;

      if (itemToCompareValueLen === controlValueLen) {
        return { compareTwoValues: true };
      }

      return null;
    };
  }

  static minSelectedCheckboxes(): ValidatorFn {
    const validator: ValidatorFn = (formArray: UntypedFormArray) => {
      const selectedCount = formArray.controls
        .map((control) => control.value)
        .reduce((prev, next) => {
          return next ? prev + 1 : prev;
        }, 0);
      return selectedCount >= 1 ? null : { notSelected: true };
    };
    return validator;
  }


  static unitCostValidator(control: AbstractControl) {
    const value = parseFloat(control.value)
    if (value < ItemMinAndMax.uCost.min) {
      return { decimalTo2Places: true }
    }
    return null
  }

  static taxRateValidator(control: AbstractControl) {
    const value = parseFloat(control.value)
    if (value < ItemMinAndMax.tRate.min || value > ItemMinAndMax.tRate.max) {
      return { taxValidator: true }
    }
    return null
  }

  static discountRateValidator(control: AbstractControl) {
    const value = parseFloat(control.value)
    if (value < ItemMinAndMax.dRate.min || value > ItemMinAndMax.dRate.max) {
      return { discountRate: true }
    }
    return null
  }

  static discountAmountValidator(control: AbstractControl) {
    const value = parseFloat(control.value)
    if (value < ItemMinAndMax.dAmount.min) {
      return { discountAmount: true }
    }
    return null
  }

  static valueInArrayCheck(arrayObject: Array<any>, validateWith: string): ValidatorFn {
    return (control:AbstractControl) : ValidationErrors | null => {

        const value = control.value;

        if (!value) {
            return null;
        }
        let isPresent = arrayObject?.some(element => element[validateWith] === value);
        return  !isPresent ? {valueInArrayCheck:true} : null;
    }
}

  static atLeastOneInputHasValue = () => {
    return (group: UntypedFormGroup) => {
      if (!Object.values(group.value).find(value => Boolean(value))) {
        return {
          atLeastOneRequired: {
            text: 'At least one control is required',
          }
        }
      }
      return null
    }
  }

  static applePayDomainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value || '').trim();

      if (!value) return null;

      // Protocols not allowed
      if (/^[a-z][a-z0-9+\-.]*:\/\//i.test(value)) {
        return { invalidDomain: 'Do not include protocol (e.g., http://, https://)' };
      }

      // IP addresses not allowed
      if (/^\d{1,3}(\.\d{1,3}){3}$/.test(value)) {
        return { invalidDomain: 'IP addresses are not allowed' };
      }

      // Localhost/internal domains not allowed
      if (/^(localhost|127\.0\.0\.1|.*\.local|.*\.internal)$/i.test(value)) {
        return { invalidDomain: 'Local/internal domains are not allowed' };
      }

      // Paths and query strings not allowed
      if (/[\/?#]/.test(value)) {
        return { invalidDomain: 'Do not include path or parameters' };
      }

      // Must be a valid domain (e.g., example.com, shop.example.com)
      const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      if (!domainRegex.test(value)) {
        return { invalidDomain: 'Invalid domain format' };
      }

      return null;
    };
  }

  static applicationTagValidator(options?: {
    allowUnreleasedDefault?: boolean;
  }): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = typeof control.value === 'string' ? control.value.trim() : '';

      if (!value || value === 'Custom') {
        return options?.allowUnreleasedDefault
          ? null
          : { applicationTag: CustomValidators.applicationTagRequiredMessage };
      }

      if (value.length > CustomValidators.applicationTagMaxLength) {
        return { applicationTag: CustomValidators.applicationTagLengthMessage };
      }

      if (['Unreleased', 'Published', 'Unstable', 'Stable'].includes(value)) {
        return null;
      }

      if (!CustomValidators.applicationTagPattern.test(value)) {
        return { applicationTag: CustomValidators.applicationTagCharsetMessage };
      }

      return null;
    };
  }

}
